import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  theme,
  useMediaQuery,
} from '@everlywell/ui-kit';
import React, { useState, useEffect } from 'react';

import PricingSelectorModalOptions, {
  PricingSelectorModalOptionsProps,
} from './PricingSelectorModalOptions';

export type PricingSelectorModalProps = {
  title: string;
  options?: PricingSelectorModalOptionsProps[];
  description?: string;
  isOpen?: boolean;
  onClose?: () => void;
};
/**
 * PricingSelectorModal component is used to display a modal with pricing options.
 * It can be used in both mobile and desktop views.
 *
 * @param {string} title - The title of the modal.
 * @param {PricingSelectorModalOptionsProps[]} options - The options to be displayed in the modal.
 * @param {string} description - A disclaimer for the modal.
 * @param {boolean} isOpen - Whether the modal is open or not.
 * @param {function} onClose - Callback function to be called when the modal is closed.
 */
const PricingSelectorModal = ({
  title,
  options,
  description,
  isOpen = false,
  onClose,
}: PricingSelectorModalProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(isOpen);
  const [isMobileOrTablet] = useMediaQuery(
    `(max-width: ${theme.breakpoints.largeTablet})`,
  );

  useEffect(() => {
    setInternalIsOpen(isOpen);
  }, [isOpen]);

  const handleClose = () => {
    setInternalIsOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const optionsContent = (
    <Flex flexDirection="column" gap="4">
      {options?.map((option, index) => (
        <PricingSelectorModalOptions key={index} {...option} />
      ))}
    </Flex>
  );

  return isMobileOrTablet ? (
    <Drawer
      isOpen={isOpen}
      placement="bottom"
      onClose={handleClose}
      blockScrollOnMount={false}
      autoFocus={false}
    >
      <DrawerOverlay />
      <DrawerContent
        backgroundColor="tints.white"
        borderRadius="3xl"
        paddingX="4"
        paddingTop="6"
        paddingBottom="10"
      >
        <DrawerHeader padding="0">
          <Text fontSize="lg">{title}</Text>
          <DrawerCloseButton
            width="32px"
            height="32px"
            backgroundColor="tints.cream"
            sx={{
              svg: {
                fontSize: '16px',
              },
            }}
          />
        </DrawerHeader>
        <DrawerBody paddingTop="0" padding="0">
          {optionsContent}

          <Text
            color="tints.darkGrey"
            fontSize="sm"
            marginTop="6"
            textAlign="center"
          >
            {description}
          </Text>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  ) : (
    <Modal isOpen={internalIsOpen} onClose={() => handleClose()} size="md">
      <ModalOverlay />
      <ModalContent
        backgroundColor="tints.white"
        padding="6"
        borderRadius="3xl"
      >
        <ModalHeader padding="0" marginBottom="6">
          <Text fontSize="lg">{title}</Text>
          <ModalCloseButton
            width="32px"
            height="32px"
            backgroundColor="tints.cream"
            marginTop="3"
          />
        </ModalHeader>
        {optionsContent}

        <Text
          color="tints.darkGrey"
          fontSize="md"
          marginTop="6"
          textAlign="center"
        >
          {description}
        </Text>
      </ModalContent>
    </Modal>
  );
};

export default PricingSelectorModal;
